// IMPEDIMENT TRACKING - Frontend API Integration
// Phase 9: Impediment Tracking

import axios from 'axios'
import {setupAxios} from '../app/modules/auth'
import {API_BASE_URL} from '../utils/contants'

// Enums
export enum ImpedimentStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export enum ImpedimentPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

// Interfaces
export interface Impediment {
  id: string
  userId: string
  timesheetEntryId?: string
  taskId?: string
  title: string
  description: string
  priority: ImpedimentPriority
  status: ImpedimentStatus
  impactedArea?: string
  tags: string[]
  resolutionNotes?: string
  createdAt: string
  updatedAt: string
  resolvedAt?: string
  user: {
    id: string
    firstName: string
    lastName: string
    email?: string
  }
  timesheetEntry?: any
  task?: any
  comments?: ImpedimentComment[]
}

export interface ImpedimentComment {
  id: string
  impedimentId: string
  userId: string
  comment: string
  createdAt: string
  user: {
    id: string
    firstName: string
    lastName: string
  }
}

export interface ImpedimentCreateInput {
  userId: string
  timesheetEntryId?: string
  taskId?: string
  title: string
  description: string
  priority: ImpedimentPriority
  impactedArea?: string
  tags?: string[]
}

export interface ImpedimentUpdateInput {
  title?: string
  description?: string
  priority?: ImpedimentPriority
  status?: ImpedimentStatus
  impactedArea?: string
  tags?: string[]
  resolutionNotes?: string
  userId: string
}

export interface ImpedimentQueryParams {
  userId?: string
  status?: ImpedimentStatus
  priority?: ImpedimentPriority
  startDate?: string
  endDate?: string
  search?: string
  page?: number
  limit?: number
}

// API Functions

export const createImpediment = async (data: ImpedimentCreateInput): Promise<Impediment> => {
  const {token} = setupAxios()
  const response = await axios.post(`${API_BASE_URL}/impediment`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
  return response.data.data
}

export const getImpediments = async (
  params?: ImpedimentQueryParams
): Promise<{
  impediments: Impediment[]
  total: number
  page: number
  limit: number
  totalPages: number
  summary: Record<string, number>
}> => {
  const {token} = setupAxios()
  const response = await axios.get(`${API_BASE_URL}/impediment`, {
    params,
    headers: {Authorization: `Bearer ${token}`},
  })
  return response.data.data
}

export const getImpedimentById = async (id: string): Promise<Impediment> => {
  const {token} = setupAxios()
  const response = await axios.get(`${API_BASE_URL}/impediment/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
  return response.data.data
}

export const updateImpediment = async (
  id: string,
  data: ImpedimentUpdateInput
): Promise<Impediment> => {
  const {token} = setupAxios()
  const response = await axios.put(`${API_BASE_URL}/impediment/${id}`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
  return response.data.data
}

export const deleteImpediment = async (id: string, userId: string): Promise<void> => {
  const {token} = setupAxios()
  await axios.delete(`${API_BASE_URL}/impediment/${id}`, {
    params: {userId},
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const addImpedimentComment = async (
  id: string,
  userId: string,
  comment: string
): Promise<ImpedimentComment> => {
  const {token} = setupAxios()
  const response = await axios.post(
    `${API_BASE_URL}/impediment/${id}/comment`,
    {userId, comment},
    {
      headers: {Authorization: `Bearer ${token}`},
    }
  )
  return response.data.data
}

export const getImpedimentsByTimesheetEntry = async (
  timesheetEntryId: string
): Promise<Impediment[]> => {
  const {token} = setupAxios()
  const response = await axios.get(`${API_BASE_URL}/impediment/timesheet/${timesheetEntryId}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
  return response.data.data
}

export const getImpedimentsByTask = async (taskId: string): Promise<Impediment[]> => {
  const {token} = setupAxios()
  const response = await axios.get(`${API_BASE_URL}/impediment/task/${taskId}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
  return response.data.data
}


